// Runtime accent-color theming. The `ml-primary` / `ml-primary-container`
// Tailwind tokens resolve to the CSS variables set here, so changing them
// re-colors the interface live. Values are space-separated RGB channels so
// Tailwind opacity modifiers (e.g. bg-ml-primary/10) keep working.

export interface ThemeColor {
  key: string
  label: string
  primary: string // "r g b"
  container: string // "r g b"
  complement: string // "r g b" — harmonious counter-hue that the surfaces/background tint toward
  // Optional overrides used by the specialty presets. When present, the page/card
  // surfaces are painted with these EXACT values instead of being derived from
  // `complement`, so each specialty theme reproduces its precise palette. Absent on
  // the accent swatches, which keep deriving their surfaces exactly as before.
  background?: string // "r g b" — explicit page background (else derived from complement)
  surface?: string // "r g b" — explicit card/popover surface (else near-white derived)
  gradient?: string // "r g b" — decorative light stop shown in the palette thumbnail
}

// ── Specialty palettes ─────────────────────────────────────────────────────────
// The only selectable themes. Each flows through the same token pipeline
// (computeThemeVars → --ml-* CSS vars). `primary`/`container` carry white UI text
// across the app, so both are kept dark enough for WCAG 2.1 AA (>=4.5:1 on white).
// Where a brand Primary was too light for that (Pediatrics, Gynecology) it is
// deepened to an AA-safe shade of the same hue; the literal brand tint still
// appears as large headings/soft tints on light surfaces. `complement` is the
// accent that paints the page bloom; `gradient` is decorative (thumbnail only).
export const THEME_COLORS: ThemeColor[] = [
  {
    key: 'peds', label: 'Pediatría',
    primary: '12 122 120', // #0C7A78 — brand #17A2A0 deepened for AA (5.2:1 on white)
    container: '10 106 104', // #0A6A68
    complement: '255 158 109', // accent #FF9E6D
    gradient: '63 199 180', // #3FC7B4
    background: '245 251 250', // #F5FBFA
    surface: '255 255 255', // #FFFFFF
  },
  {
    key: 'gyne', label: 'Ginecología',
    primary: '168 77 124', // #A84D7C — brand #B85C8A deepened for AA (5.2:1 on white)
    container: '140 63 102', // #8C3F66
    complement: '233 161 120', // accent #E9A178
    gradient: '217 139 174', // #D98BAE
    background: '251 245 248', // #FBF5F8
    surface: '255 255 255', // #FFFFFF
  },
  {
    key: 'general', label: 'General',
    primary: '16 110 121', // #106E79 — brand #14808D deepened for AA headroom (5.95:1 on white)
    container: '12 92 102', // #0C5C66
    complement: '52 179 126', // accent #34B37E
    gradient: '42 169 160', // #2AA9A0
    background: '244 250 251', // #F4FAFB
    surface: '255 255 255', // #FFFFFF
  },
  {
    key: 'professional', label: 'Profesional',
    primary: '30 58 95', // #1E3A5F (11.5:1 on white)
    container: '44 82 130', // #2C5282 — spec gradient, AA-safe (7.97:1) as brand-gradient stop
    complement: '201 169 77', // accent #C9A94D (gold — decorative only)
    gradient: '44 82 130', // #2C5282
    background: '244 246 249', // #F4F6F9
    surface: '255 255 255', // #FFFFFF
  },
]

const STORAGE_KEY = 'hms-theme-color'

// Fired whenever the active theme changes so every open picker (accent swatches +
// specialty selector) reflects the single active selection without a shared store.
const THEME_EVENT = 'hms-theme-change'

// Subscribe to theme changes; returns an unsubscribe fn. Used by the pickers so
// selecting a specialty theme clears the accent swatch checkmark and vice versa.
export function onThemeColorChange(cb: (key: string) => void): () => void {
  const handler = (e: Event) => cb((e as CustomEvent<string>).detail)
  window.addEventListener(THEME_EVENT, handler)
  return () => window.removeEventListener(THEME_EVENT, handler)
}

// ── Dark mode ────────────────────────────────────────────────────────────────
const MODE_KEY = 'hms-theme-mode'
const MODE_EVENT = 'hms-theme-mode-change'
export type ThemeMode = 'light' | 'dark'

// The app's baseline theme — used by the Reset control and as the fallback for
// new users (or anyone whose saved key predates the specialty themes).
export const DEFAULT_THEME_KEY = 'general'

export function getSavedThemeMode(): ThemeMode {
  try {
    return localStorage.getItem(MODE_KEY) === 'dark' ? 'dark' : 'light'
  } catch {
    return 'light'
  }
}

export function onThemeModeChange(cb: (mode: ThemeMode) => void): () => void {
  const handler = (e: Event) => cb((e as CustomEvent<ThemeMode>).detail)
  window.addEventListener(MODE_EVENT, handler)
  return () => window.removeEventListener(MODE_EVENT, handler)
}

const WHITE: [number, number, number] = [255, 255, 255]
const BLACK: [number, number, number] = [0, 0, 0]

function parseRgb(value: string): [number, number, number] {
  const [r, g, b] = value.split(/\s+/).map(Number)
  return [r, g, b]
}

// Blend an "r g b" color toward `target` by weight t (0 = color, 1 = target).
// Returns space-separated channels for use in CSS var()s.
function mix(color: [number, number, number], target: [number, number, number], t: number): string {
  const c = (a: number, b: number) => Math.round(a * (1 - t) + b * t)
  return `${c(color[0], target[0])} ${c(color[1], target[1])} ${c(color[2], target[2])}`
}

// Convert "r g b" (0-255) to the "H S% L%" form shadcn's --primary expects.
function rgbToHsl(value: string): string {
  const [r, g, b] = parseRgb(value).map((c) => c / 255)
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  const d = max - min
  let h = 0
  let s = 0
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0)
    else if (max === g) h = (b - r) / d + 2
    else h = (r - g) / d + 4
    h /= 6
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`
}

export function getSavedThemeColor(): string {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    // Fall back to the default if empty or a retired key (e.g. an old accent preset).
    return saved && THEME_COLORS.some((c) => c.key === saved) ? saved : DEFAULT_THEME_KEY
  } catch {
    return DEFAULT_THEME_KEY
  }
}

// Dark-mode neutral text/border tokens (theme-independent; light values live in
// :root). Kept here so both apply and preview share one source of truth.
const DARK = {
  onSurface: '226 232 235', // main text
  onSurfaceVariant: '155 166 174', // dimmed text
  outline: '96 106 114', // borders
  outlineVariant: '52 60 68', // subtle dividers
  onPrimary: '10 16 20', // dark text on filled (now-light) primary CTAs
  error: '242 184 181', // MD3 dark error
}

// Every custom property the engine manages. Cleared before each apply so switching
// mode/theme never leaves a stale dark value bleeding into light (or vice-versa).
const MANAGED_KEYS = [
  '--ml-primary', '--ml-primary-container', '--ml-secondary-container', '--ml-on-secondary-container',
  '--ml-surface-tint', '--ml-on-primary-container', '--ml-primary-fixed', '--ml-primary-fixed-dim',
  '--ml-surface', '--ml-surface-container-lowest', '--ml-surface-container-low', '--ml-surface-container',
  '--ml-surface-container-high', '--ml-surface-container-highest', '--ml-accent-soft',
  '--ml-on-surface', '--ml-on-surface-variant', '--ml-outline', '--ml-outline-variant', '--ml-on-primary', '--ml-error',
  '--background', '--foreground', '--card', '--card-foreground', '--popover', '--popover-foreground',
  '--muted', '--muted-foreground', '--accent', '--accent-foreground', '--border', '--input', '--ring',
  '--sidebar-background', '--sidebar-foreground', '--sidebar-accent', '--sidebar-accent-foreground', '--sidebar-border',
  '--primary', '--primary-foreground',
  // Vibrant/brand tokens (theme's gradient-a + accent, and the login "scene" palette)
  '--ml-gradient', '--ml-accent',
  '--ml-brand-deep', '--ml-brand-deep-2', '--ml-brand-mid', '--ml-brand-hi', '--ml-brand-glow', '--ml-brand-accent',
]

// Pure function: given a theme + mode, return the full CSS-variable map. Used both
// to paint the document root (applyThemeColor) and to render the scoped, live
// preview mockup — so preview and reality can never diverge. No side effects.
export function computeThemeVars(color: ThemeColor, isDark: boolean): Record<string, string> {
  const primary = parseRgb(color.primary)
  const complement = parseRgb(color.complement)
  const gradient = parseRgb(color.gradient ?? color.primary)
  const v: Record<string, string> = {}

  // Accent family derived from the chosen primary (both modes reference these).
  v['--ml-on-secondary-container'] = color.primary
  v['--ml-on-primary-container'] = mix(primary, WHITE, 0.5)

  // Vibrant tokens — the theme's bright "gradient-a" and accent, exposed so key
  // elements (gradient CTAs, active nav, badges, focus glows) can pop with the
  // theme. Same in both modes; they're brand colors, not surface-dependent.
  v['--ml-gradient'] = color.gradient ?? color.primary
  v['--ml-accent'] = color.complement

  // Login "scene" palette — a STABLE immersive-dark set derived from the brand
  // colors (mode-independent, so the login always renders as a saturated dark
  // panel that recolors per theme). `deep` carries the AA-safe text region;
  // `hi`/`accent` are vibrant, decorative-only (blobs, glow, gradient corner).
  v['--ml-brand-deep'] = mix(primary, BLACK, 0.62) // canvas floor — white text is AA here
  v['--ml-brand-deep-2'] = mix(primary, BLACK, 0.4)
  v['--ml-brand-mid'] = color.primary // white text is AA (every theme primary ≥5:1)
  v['--ml-brand-hi'] = color.gradient ?? color.primary // bright gradient-a (no small text)
  v['--ml-brand-glow'] = mix(gradient, WHITE, 0.35) // bright glow / accent wordmark on dark
  v['--ml-brand-accent'] = color.complement // floating accent blobs

  if (isDark) {
    // In dark mode the primary flips to a LIGHT tone so headings/labels/icons that
    // use `text-ml-primary` stay legible on dark surfaces (the dominant pattern).
    // Filled `bg-ml-primary` CTAs get dark text via the `.dark` compat rule in CSS.
    const lightPrimary = mix(primary, WHITE, 0.55)
    // Surfaces: near-black page, elevated cards, each tinted ~10% toward the primary
    // hue so the palette still reads. Ramp mirrors light (lowest = brightest card).
    const surf = (base: [number, number, number]) => mix(primary, base, 0.9)
    v['--ml-primary'] = lightPrimary
    v['--ml-primary-container'] = mix(primary, WHITE, 0.42)
    v['--ml-secondary-container'] = surf([44, 51, 58])
    v['--ml-on-secondary-container'] = lightPrimary
    v['--ml-surface-tint'] = lightPrimary
    v['--ml-primary-fixed'] = surf([46, 54, 61]) // dark chip; text-ml-primary (light) sits on it
    v['--ml-primary-fixed-dim'] = surf([36, 42, 48])
    v['--ml-surface'] = surf([15, 18, 21])
    v['--ml-surface-container-lowest'] = surf([42, 49, 56])
    v['--ml-surface-container-low'] = surf([36, 42, 48])
    v['--ml-surface-container'] = surf([31, 37, 42])
    v['--ml-surface-container-high'] = surf([27, 32, 37])
    v['--ml-surface-container-highest'] = surf([23, 27, 31])
    v['--ml-accent-soft'] = mix(complement, primary, 0.35)
    v['--ml-on-surface'] = DARK.onSurface
    v['--ml-on-surface-variant'] = DARK.onSurfaceVariant
    v['--ml-outline'] = DARK.outline
    v['--ml-outline-variant'] = DARK.outlineVariant
    v['--ml-on-primary'] = DARK.onPrimary
    v['--ml-error'] = DARK.error
    // shadcn structural (HSL).
    v['--background'] = rgbToHsl(surf([15, 18, 21]))
    v['--foreground'] = rgbToHsl(DARK.onSurface)
    v['--card'] = rgbToHsl(surf([42, 49, 56]))
    v['--card-foreground'] = rgbToHsl(DARK.onSurface)
    v['--popover'] = rgbToHsl(surf([42, 49, 56]))
    v['--popover-foreground'] = rgbToHsl(DARK.onSurface)
    v['--muted'] = rgbToHsl(surf([31, 37, 42]))
    v['--muted-foreground'] = rgbToHsl(DARK.onSurfaceVariant)
    v['--accent'] = rgbToHsl(surf([31, 37, 42]))
    v['--accent-foreground'] = rgbToHsl(DARK.onSurface)
    v['--border'] = rgbToHsl(DARK.outlineVariant)
    v['--input'] = rgbToHsl(DARK.outlineVariant)
    v['--ring'] = rgbToHsl(lightPrimary)
    v['--sidebar-background'] = rgbToHsl(surf([22, 26, 30]))
    v['--sidebar-foreground'] = rgbToHsl(DARK.onSurface)
    v['--sidebar-accent'] = rgbToHsl(surf([36, 42, 48]))
    v['--sidebar-accent-foreground'] = rgbToHsl(DARK.onSurface)
    v['--sidebar-border'] = rgbToHsl(DARK.outlineVariant)
    v['--primary'] = rgbToHsl(lightPrimary)
    v['--primary-foreground'] = rgbToHsl(DARK.onPrimary)
    return v
  }

  // ── LIGHT MODE — identical output to the previous engine ──────────────────────
  v['--ml-primary'] = color.primary
  v['--ml-primary-container'] = color.container
  // 0.91 (was 0.86): a hair lighter so `text-ml-on-secondary-container` (= primary)
  // clears AA on these panels even with the mid-tone specialty primaries.
  v['--ml-secondary-container'] = mix(primary, WHITE, 0.91)
  v['--ml-surface-tint'] = color.primary
  v['--ml-primary-fixed'] = mix(primary, WHITE, 0.74)
  v['--ml-primary-fixed-dim'] = mix(primary, WHITE, 0.55)
  // Neutral text/border tokens = their :root defaults (light unchanged).
  v['--ml-on-surface'] = '26 28 25'
  v['--ml-on-surface-variant'] = '65 72 69'
  v['--ml-outline'] = '114 121 116'
  v['--ml-outline-variant'] = '193 200 195'
  v['--ml-on-primary'] = '255 255 255'
  v['--ml-error'] = '186 26 26'

  const bgStr = color.background
  const surfaceStr = color.surface
  if (bgStr && surfaceStr) {
    // Specialty preset: paint page/cards with the palette's EXACT background/surface.
    const bg = parseRgb(bgStr)
    const surface = parseRgb(surfaceStr)
    // Cards get a very subtle primary tint (not pure white) so each theme has its
    // own recognizable feel; still ~96% white, so near-black text stays far above AA.
    const card = mix(surface, primary, 0.035)
    v['--ml-surface'] = bgStr
    v['--ml-surface-tint'] = mix(bg, complement, 0.1)
    v['--ml-surface-container-lowest'] = card
    v['--ml-surface-container-low'] = mix(surface, bg, 0.55)
    v['--ml-surface-container'] = bgStr
    v['--ml-surface-container-high'] = mix(bg, complement, 0.06)
    v['--ml-surface-container-highest'] = mix(bg, complement, 0.12)
    v['--background'] = rgbToHsl(bgStr)
    v['--card'] = rgbToHsl(card)
    v['--popover'] = rgbToHsl(card)
    v['--muted'] = rgbToHsl(mix(surface, bg, 0.6))
    v['--accent'] = rgbToHsl(mix(surface, bg, 0.6))
    v['--sidebar-background'] = rgbToHsl(mix(bg, complement, 0.05))
    v['--sidebar-accent'] = rgbToHsl(mix(bg, complement, 0.12))
    // A touch more saturated (0.5 vs 0.58) so the page's theme bloom reads clearly.
    v['--ml-accent-soft'] = mix(complement, WHITE, 0.5)
    // Near-white pill tint so status pills (text-ml-primary) clear AA — see note.
    v['--ml-primary-fixed'] = mix(primary, WHITE, 0.9)
    v['--ml-primary-fixed-dim'] = mix(primary, WHITE, 0.8)
  } else {
    // Accent presets: derive surfaces from the complement (page tints; chrome stays near-white).
    v['--ml-surface'] = mix(complement, WHITE, 0.86)
    v['--ml-surface-tint'] = mix(complement, WHITE, 0.74)
    v['--ml-surface-container-lowest'] = mix(complement, WHITE, 0.985)
    v['--ml-surface-container-low'] = mix(complement, WHITE, 0.95)
    v['--ml-surface-container'] = mix(complement, WHITE, 0.92)
    v['--ml-surface-container-high'] = mix(complement, WHITE, 0.9)
    v['--ml-surface-container-highest'] = mix(complement, WHITE, 0.88)
    v['--background'] = rgbToHsl(mix(complement, WHITE, 0.86))
    v['--card'] = rgbToHsl(mix(complement, WHITE, 0.985))
    v['--popover'] = rgbToHsl(mix(complement, WHITE, 0.985))
    v['--muted'] = rgbToHsl(mix(complement, WHITE, 0.93))
    v['--accent'] = rgbToHsl(mix(complement, WHITE, 0.93))
    v['--sidebar-background'] = rgbToHsl(mix(complement, WHITE, 0.92))
    v['--sidebar-accent'] = rgbToHsl(mix(complement, WHITE, 0.88))
    v['--ml-accent-soft'] = mix(complement, WHITE, 0.58)
  }
  v['--primary'] = rgbToHsl(color.primary)
  v['--primary-foreground'] = '0 0% 100%'
  return v
}

// Briefly enable CSS transitions on color tokens so an apply eases rather than
// snapping. Auto-removed after the transition window. Reduced-motion users get no
// transition (the CSS rule is behind a prefers-reduced-motion guard).
let transitionTimer: ReturnType<typeof setTimeout> | undefined
function enableThemeTransition(root: HTMLElement): void {
  root.classList.add('theme-transition')
  if (transitionTimer) clearTimeout(transitionTimer)
  transitionTimer = setTimeout(() => root.classList.remove('theme-transition'), 320)
}

export function applyThemeColor(key: string, opts?: { animate?: boolean }): void {
  const color = THEME_COLORS.find((c) => c.key === key) ?? THEME_COLORS[0]
  const root = document.documentElement
  const isDark = root.classList.contains('dark')
  if (opts?.animate) enableThemeTransition(root)

  const vars = computeThemeVars(color, isDark)
  MANAGED_KEYS.forEach((k) => root.style.removeProperty(k))
  for (const [prop, value] of Object.entries(vars)) root.style.setProperty(prop, value)

  try {
    localStorage.setItem(STORAGE_KEY, color.key)
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent(THEME_EVENT, { detail: color.key }))
}

// Toggle light/dark. Flips the `.dark` class (the app's existing convention),
// persists it, and re-applies the active theme so the mode-specific vars refresh.
export function applyThemeMode(mode: ThemeMode, opts?: { animate?: boolean }): void {
  const root = document.documentElement
  if (opts?.animate) enableThemeTransition(root)
  root.classList.toggle('dark', mode === 'dark')
  root.style.colorScheme = mode
  try {
    localStorage.setItem(MODE_KEY, mode)
  } catch {
    /* ignore */
  }
  applyThemeColor(getSavedThemeColor())
  window.dispatchEvent(new CustomEvent(MODE_EVENT, { detail: mode }))
}

// Apply the saved (or default) theme + mode before the app renders.
export function initThemeColor(): void {
  const root = document.documentElement
  const mode = getSavedThemeMode()
  root.classList.toggle('dark', mode === 'dark')
  root.style.colorScheme = mode
  applyThemeColor(getSavedThemeColor())
}
